import { NetworkType, zeroExWrapperUrl } from 'abis/set/sets'
import Big from 'big.js'
import { atom } from 'jotai'
import { type AnatomyWithExtra, defaultIndexAnatomy } from 'services/query-v2/anatomy'
import { createAtomsWithQuery } from 'services/query-v2/atomic-factory'
import { QUERY_KEY_OUTPUT_AMOUNT } from 'services/query-v2/keys'
import { DEFAULT_REFETCH_INTERVAL } from 'services/query-v2/refetch-intervals'
import { convertAmountToWei, objToQueryString } from 'utils'
import { isAddressEqual, zeroAddress } from 'viem'
import {
  type OutputAmountResponse,
  type OutputRequestAtoms,
  type PriceParams,
  type RawQuotesResponse,
  defaultPriceParams,
  parseOutput,
} from './model'
import { ZERO_EX_AFFILIATE_AVAX_ADDRESS, ZERO_EX_AFFILIATE_BASE_ADDRESS } from 'app-constants'

const indexV1OutputAmountQuery = async <T>({
  queryKey: [, params],
}: {
  queryKey: [string, PriceParams, AnatomyWithExtra]
}) => {
  const url = zeroExWrapperUrl

  if (
    params.sellAmount.eq(0) ||
    !params.userAddress ||
    isAddressEqual(params.userAddress, zeroAddress)
  ) {
    const defaultAmountResponse: OutputAmountResponse = {
      chainId: params.chainId,
      currency: params.sellCurrency,
      buyAmount: Big(0),
    }
    return defaultAmountResponse as T
  }
  const urlParams = {
    chainId: params.chainId,
    sellToken: isAddressEqual(params.sellCurrency.address, zeroAddress)
      ? '0xEeeeeEeeeEeEeeEeEeEeeEEEeeeeEeeeeeeeEEeE'
      : params.sellCurrency.address,
    buyToken: isAddressEqual(params.buyCurrency.address, zeroAddress)
      ? '0xEeeeeEeeeEeEeeEeEeEeeEEEeeeeEeeeeeeeEEeE'
      : params.buyCurrency.address,
    sellAmount: convertAmountToWei(params.sellAmount, params.sellCurrency.decimals).toFixed(),
    affiliateAddress: params.affiliateAddress,
    slippageBps: params.slippagePercentage * 1000,
    taker: params.userAddress,
    txOrigin: params.userAddress,
    swapFeeToken: isAddressEqual(params.sellCurrency.address, zeroAddress)
      ? '0xEeeeeEeeeEeEeeEeEeEeeEEEeeeeEeeeeeeeEEeE'
      : params.sellCurrency.address,
    swapFeeBps: 30,
    swapFeeRecipient:
      params.chainId === NetworkType.Avalanche
        ? ZERO_EX_AFFILIATE_AVAX_ADDRESS
        : params.chainId === NetworkType.Base
          ? ZERO_EX_AFFILIATE_BASE_ADDRESS
          : undefined,
  }

  const response = await fetch(`${url}/swap/allowance-holder/quote?${objToQueryString(urlParams)}`)

  const json = (await response.json()) as RawQuotesResponse

  return parseOutput(params, json) as T
}

export const indexV1OutputAmountAtomsFactory = () => {
  const paramsAtom = atom<PriceParams>(defaultPriceParams)
  const indexAnatomyAtom = atom<AnatomyWithExtra>(defaultIndexAnatomy)

  const mainProperties = createAtomsWithQuery<
    OutputAmountResponse,
    PriceParams,
    AnatomyWithExtra,
    null,
    [string, PriceParams, AnatomyWithExtra]
  >(
    { first: paramsAtom, second: indexAnatomyAtom },
    QUERY_KEY_OUTPUT_AMOUNT,
    indexV1OutputAmountQuery<OutputAmountResponse>,
    DEFAULT_REFETCH_INTERVAL,
  )

  return {
    params: paramsAtom,
    indexAnatomy: indexAnatomyAtom,
    ...mainProperties,
  } as OutputRequestAtoms
}
